import { BridgeFiatRail } from 'services/merchants/types';

import { DayOfMonth, FiatPayoutFrequency, Weekday } from 'types/time';

import { makeLabels } from 'utils/i18n';

const NAMESPACE = 'settlement.payout.fiat';
export const LABELS = makeLabels(
  {
    GENERIC_ERORR: ['genericError', 'An error occurred while processing your request. Please try again later.'],
    CURRENCY_LABEL: ['currency', 'Currency'],
    PAYMENT_RAIL: ['paymentRail', 'Payment Rail'],
    FEES: ['fees', 'Fees'],
    PAYMENT_RAIL_OPTIONS: {
      [BridgeFiatRail.ACH]: ['paymentRailOptions.ach', 'ACH'],
      [BridgeFiatRail.ACH_SAME_DATE]: ['paymentRailOptions.achSameDate', 'ACH Same Day'],
      [BridgeFiatRail.WIRE]: ['paymentRailOptions.wire', 'Wire'],
      [BridgeFiatRail.SWIFT]: ['paymentRailOptions.swift', 'SWIFT'],
      [BridgeFiatRail.SEPA]: ['paymentRailOptions.sepa', 'SEPA'],
      [BridgeFiatRail.SPEI]: ['paymentRailOptions.spei', 'SPEI'],
      [BridgeFiatRail.PIX]: ['paymentRailOptions.pix', 'PIX'],
      [BridgeFiatRail.BRE_B]: ['paymentRailOptions.breB', 'Bre-B'],
    },
    ACTIONS: {
      CONTINUE: ['actions.continue', 'Continue'],
      CANCEL: ['actions.cancel', 'Cancel'],
      ADD: ['actions.add', 'Add'],
      BACK: ['actions.back', 'Back'],
      BACK_TO_BANK_ACCOUNT: ['actions.backToBankAccount', 'Back to Bank Account'],
      SKIP: ['actions.skip', 'Skip'],
      SAVE: ['actions.save', 'Save'],
      INSERT_TITAN_DATA: ['actions.insertTitanData', 'Insert Titan Data'],
    },
    INCOMPATIBLE_PAYMENT_RAIL_TITLE: ['incompatiblePaymentRailTitle', 'Incompatible Payment Rail'],
    INCOMPATIBLE_PAYMENT_RAIL_DESCRIPTION: [
      'incompatiblePaymentRailDescription',
      'This bank account you entered is not compatibile with the selected payment rail.<br/><br/>' +
        'Please select a different bank account or choose another payout rail.',
    ],
    MEMO_LABEL: ['memoLabel', 'Memo/Message'],
    MEMO_SUB_LABEL: ['memoSubLabel', '{{paymentRail}} - Accepted length between {{minLength}} and {{maxLength}} characters.'],
    MEMO_PLACEHOLDER: ['memoPlaceholder', 'Enter Memo'],
    MEMO_VALIDATION_ERROR: [
      'memoValidationError',
      'Your input must be between {{min}} and {{max}} characters long. Please adjust your text accordingly.',
    ],
    MEMO_REQUIRED_ERROR: ['memoRequiredError', 'Provide a memo to proceed.'],
    MEMO_LINE_COUNT_ERROR: ['memoLineCountError', 'Memo can have at most {{max}} lines.'],
    MEMO_INVALID_CHARACTERS: ['memoInvalidCharacters', 'Memo contains invalid characters.'],
    INCORRECT_PAYOUT_SCHEDULE_ERROR: ['incorrectPayoutScheduleError', 'Please provide a valid payout schedule.'],
    TIME_PICKER_LABEL: ['timePickerLabel', 'Time'],
    FREQUENCY_LABEL: ['frequencyLabel', 'Frequency'],
    FIAT_FREQUENCY_OPTIONS: {
      [FiatPayoutFrequency.Daily]: ['frequencyOptions.daily', 'Daily'],
      [FiatPayoutFrequency.Weekly]: ['frequencyOptions.weekly', 'Weekly'],
      [FiatPayoutFrequency.Monthly]: ['frequencyOptions.monthly', 'Monthly'],
    },
    DAY_SELECTION_LABEL: ['daySelectionLabel', 'Day'],
    ACCOUNT_SELECTION_LABEL: ['accountSelectionLabel', '{{currency}} Accounts'],
    ACCOUNT_SELECTION_PLACEHOLDER: ['accountSelectionPlaceholder', 'Select one'],
    NEW_BANK_ACCOUNT_LABEL: ['newBankAccountLabel', 'New Bank Account'],
    WEEKDAY_LABELS: {
      [Weekday.Sunday]: ['weekdayLabels.sunday', 'Sunday'],
      [Weekday.Monday]: ['weekdayLabels.monday', 'Monday'],
      [Weekday.Tuesday]: ['weekdayLabels.tuesday', 'Tuesday'],
      [Weekday.Wednesday]: ['weekdayLabels.wednesday', 'Wednesday'],
      [Weekday.Thursday]: ['weekdayLabels.thursday', 'Thursday'],
      [Weekday.Friday]: ['weekdayLabels.friday', 'Friday'],
      [Weekday.Saturday]: ['weekdayLabels.saturday', 'Saturday'],
    },
    DAYS_OF_MONTH: {
      [DayOfMonth.FirstOfMonth]: ['daysOfMonth.firstOfMonth', 'First of Month'],
      [DayOfMonth.First]: ['daysOfMonth.first', '1st'],
      [DayOfMonth.LastOfMonth]: ['daysOfMonth.lastOfMonth', 'Last of Month'],
      [DayOfMonth.Second]: ['daysOfMonth.second', '2nd'],
      [DayOfMonth.Third]: ['daysOfMonth.third', '3rd'],
      [DayOfMonth.Fourth]: ['daysOfMonth.fourth', '4th'],
      [DayOfMonth.Fifth]: ['daysOfMonth.fifth', '5th'],
      [DayOfMonth.Sixth]: ['daysOfMonth.sixth', '6th'],
      [DayOfMonth.Seventh]: ['daysOfMonth.seventh', '7th'],
      [DayOfMonth.Eighth]: ['daysOfMonth.eighth', '8th'],
      [DayOfMonth.Ninth]: ['daysOfMonth.ninth', '9th'],
      [DayOfMonth.Tenth]: ['daysOfMonth.tenth', '10th'],
      [DayOfMonth.Eleventh]: ['daysOfMonth.eleventh', '11th'],
      [DayOfMonth.Twelfth]: ['daysOfMonth.twelfth', '12th'],
      [DayOfMonth.Thirteenth]: ['daysOfMonth.thirteenth', '13th'],
      [DayOfMonth.Fourteenth]: ['daysOfMonth.fourteenth', '14th'],
      [DayOfMonth.Fifteenth]: ['daysOfMonth.fifteenth', '15th'],
      [DayOfMonth.Sixteenth]: ['daysOfMonth.sixteenth', '16th'],
      [DayOfMonth.Seventeenth]: ['daysOfMonth.seventeenth', '17th'],
      [DayOfMonth.Eighteenth]: ['daysOfMonth.eighteenth', '18th'],
      [DayOfMonth.Nineteenth]: ['daysOfMonth.nineteenth', '19th'],
      [DayOfMonth.Twentieth]: ['daysOfMonth.twentieth', '20th'],
      [DayOfMonth.TwentyFirst]: ['daysOfMonth.twentyfirst', '21st'],
      [DayOfMonth.TwentySecond]: ['daysOfMonth.twentysecond', '22nd'],
      [DayOfMonth.TwentyThird]: ['daysOfMonth.twentythird', '23rd'],
      [DayOfMonth.TwentyFourth]: ['daysOfMonth.twentyfourth', '24th'],
      [DayOfMonth.TwentyFifth]: ['daysOfMonth.twentyfifth', '25th'],
      [DayOfMonth.TwentySixth]: ['daysOfMonth.twentysixth', '26th'],
      [DayOfMonth.TwentySeventh]: ['daysOfMonth.twentyseventh', '27th'],
      [DayOfMonth.TwentyEighth]: ['daysOfMonth.twentyeighth', '28th'],
      [DayOfMonth.TwentyNinth]: ['daysOfMonth.twentyninth', '29th'],
      [DayOfMonth.Thirtieth]: ['daysOfMonth.thirtieth', '30th'],
    },
  },
  NAMESPACE,
);

export const KEYS = {
  UNASSOCIATED_ERROR: 'unassociatedError',
  NEW_ITEM: 'new',
  PAYMENT_RAIL_MEMO_LENGTH: {
    [BridgeFiatRail.ACH]: { minLength: 1, maxLength: 10 },
    [BridgeFiatRail.ACH_SAME_DATE]: { minLength: 1, maxLength: 10 },
    [BridgeFiatRail.WIRE]: { minLength: 1, maxLength: 35 },
    [BridgeFiatRail.SWIFT]: { minLength: 1, maxLength: 35 },
    [BridgeFiatRail.SEPA]: { minLength: 6, maxLength: 140 },
    [BridgeFiatRail.SPEI]: { minLength: 1, maxLength: 40 },
    [BridgeFiatRail.PIX]: { minLength: 1, maxLength: 140 },
    [BridgeFiatRail.BRE_B]: { minLength: 1, maxLength: 18 },
  },
  FORM_FIELDS: {
    MEMO: 'memo',
    SCHEDULE: {
      DATES: 'dates',
      PAYOUT_TIME: 'payoutTime',
      FREQUENCY: 'frequency',
      DAYS: 'days',
    },
  },
};

export default LABELS;
